'use client';
import { useEffect, useRef } from 'react';

const steps = [
  { icon: '🛒', num: '1', title: 'Choose Your Checkout', desc: 'Select Etsy, Gumroad or InfiniteMarket — whichever platform you trust most.' },
  { icon: '💳', num: '2', title: 'Complete Your Purchase', desc: 'Finish payment securely through your selected platform. One-time payment of $79.' },
  { icon: '📦', num: '3', title: 'Access Your Digital Bundle', desc: 'Follow the download and access instructions provided by the selected marketplace.' },
];

export default function HowItWorksSection() {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-hidden');
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    itemRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="relative py-20 lg:py-24"
      style={{ background: 'linear-gradient(180deg, #080B14 0%, #0B1020 100%)' }}
      aria-labelledby="how-works-heading"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="text-center mb-14 reveal-hidden"
          ref={(el) => { itemRefs.current[0] = el; }}
        >
          <h2 id="how-works-heading" className="section-heading mb-4" style={{ color: 'var(--foreground)' }}>
            Start in <span className="text-gradient">3 Simple Steps</span>
          </h2>
          <p className="text-lg max-w-xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            From choosing your platform to accessing 43 collections — it takes minutes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, i) => (
            <div
              key={i}
              ref={(el) => { itemRefs.current[i + 1] = el; }}
              className="reveal-hidden relative text-center"
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {/* Connector line */}
              {i < 2 && (
                <div
                  className="hidden md:block absolute top-10 h-px"
                  style={{ left: '60%', width: '80%', background: 'linear-gradient(to right, rgba(124,58,237,0.4), transparent)' }}
                  aria-hidden="true"
                />
              )}
              <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-2xl glass mb-6 text-3xl" style={{ border: '1px solid rgba(124,58,237,0.2)' }}>
                <span aria-hidden="true">{step.icon}</span>
                <span
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full text-white text-xs font-extrabold flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgb(79,70,229), rgb(124,58,237) 40%, rgb(236,72,153))' }}
                >
                  {step.num}
                </span>
              </div>
              <h3 className="font-bold text-lg mb-3" style={{ color: 'var(--foreground)' }}>{step.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
